<template>
  <div class="w-full flex flex-col h-full space-y-4 px-6 py-4 overflow-y-auto">
    <div v-if="isLoading">
      Loading....
    </div>
    <div v-else class="h-full w-full">
      <div class="grid grid-cols-12 gap-x-12 h-full">
        <div class="w-full h-full overflow-y-auto flex flex-col col-span-12">
          <div v-if="transcription.length > 0" class="w-full flex flex-col items-center">
            <div class="flex items-center space-x-2">
              <div class="bg-green-700 text-white z-20 rounded-full px-3.5 py-2 flex items-center space-x-2">
                <i class="fa-solid fa-play"></i>
                <span>Discussion Started</span>
              </div>
            </div>
            <div class="relative w-full flex flex-col items-center">
              <div class="absolute top-0 left-1/2 transform -translate-x-1/2 h-full border-r-2 border-gray-200"></div>
              <div v-for="(user, index) in transcription" :key="index" class="my-8 flex w-full" :class="{'flex-row-reverse': index % 2 !== 0}">
                <div class="w-1/2 flex items-center space-x-3 justify-end pr-8" v-if="index % 2 === 0">
                  <div v-if="user.turns === null">
                      <i class="fa-solid fa-crown text-yellow-500 text-base"></i>
                  </div>
                  <div class="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden">
                    <img :src="user.users.avatar_url" alt="Avatar" class="w-full h-full object-cover">
                  </div>
                </div>
                <div class="relative w-1/2 pl-8 flex space-x-3 items-center" v-else>
                  <div class="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden">
                    <img :src="user.users.avatar_url" alt="Avatar" class="w-full h-full object-cover">
                  </div>
                  <div v-if="user.turns === null">
                    <i class="fa-solid fa-crown text-yellow-500"></i>
                  </div>
                </div>
                <div class="relative w-1/2 pl-8" v-if="index % 2 === 0">
                  <div class="bg-white p-4 rounded-lg border border-solid border-gray-200">
                    <div class="flex justify-between items-start mb-3">
                      <p class="font-semibold font-poppins text-gray-800">{{ user.users.name }}</p>
                      <div v-if="user.turns !== null" class="rounded-tr-full rounded-br-full rounded-tl-full cursor-pointer flex items-center space-x-2 px-3 py-1" :class="[setTurnProperties(user.turns.turn_type).backgroundColor]">
                        <div v-html="setTurnIcon(user.turns.turn_type)"></div>
                        <span class="text-white text-xs font-poppins font-medium">{{ setTurnProperties(user.turns.turn_type).title }}</span>
                      </div>
                    </div>
                    <p class="font-poppins text-gray-700">{{ user.content }}</p>
                    <div class="flex justify-end mt-2">
                      <p class="font-poppins font-light text-xs text-gray-700">{{ handleShowTranscriptDate(user.created_at) }}</p>
                    </div>
                  </div>
                  <div class="absolute top-12 left-0 transform -translate-x-1/2 w-4 h-4 bg-gray-200 rounded-full border-2 border-white"></div>
                </div>
                <div class="relative w-1/2 pr-8" v-else>
                  <div class="bg-white p-4 rounded-lg border border-solid border-gray-200">
                    <div class="flex justify-between items-start mb-3">
                      <p class="font-semibold font-poppins text-gray-800">{{ user.users.name }}</p>
                      <div v-if="user.turns !== null" class="rounded-tr-full rounded-br-full rounded-tl-full cursor-pointer flex items-center space-x-2 px-3 py-1" :class="[setTurnProperties(user.turns.turn_type).backgroundColor]">
                        <div v-html="setTurnIcon(user.turns.turn_type)"></div>
                        <span class="text-white text-xs font-poppins font-medium">{{ setTurnProperties(user.turns.turn_type).title }}</span>
                      </div>
                    </div>
                    <p class="font-poppins text-gray-700">{{ user.content }}</p>
                    <div class="flex justify-end mt-2">
                      <p class="font-poppins font-light text-xs text-gray-700">{{ handleShowTranscriptDate(user.created_at) }}</p>
                    </div>
                  </div>
                  <div class="absolute top-12 right-0 transform translate-x-1/2 w-4 h-4 bg-gray-200 rounded-full border-2 border-white"></div>
                </div>
              </div>
            </div>
            <button @click="handleRefreshDiscussion()" v-if="getCurrentDiscussion.is_live === true" class="bg-gray-50 hover:bg-gray-100 text-gray-800 font-poppins z-20 rounded-full px-3.5 py-2 flex items-center space-x-1 border border-solid border-gray-200">
              <span>Refresh</span>
              <i class="fa-solid fa-rotate-right"></i>
            </button>
            <div v-else class="bg-red-500 text-white z-20 rounded-full px-3.5 py-2 flex items-center space-x-2">
              <i class="fa-solid fa-stop"></i>
              <span>Discussion Stopped</span>
            </div>
          </div>
          <div v-else class="w-full lg:-ml-10 ml-0 h-full flex flex-col py-64 space-y-3 items-center justify-center">
            <div class="w-full flex flex-col space-y-3 items-center text-center lg:w-6/12">
              <img class="w-12 h-12" src="/static/voice-recognition.png"/>
              <h3 class="text-3xl font-poppins font-semibold text-gray-800">
                Transcription
              </h3>
              <div class="font-poppins text-gray-800 font-light">
                Unlock the power of automatic transcription!<br/>
                Easily convert your verbal discussion into text to make it easier to track and assess. <br/>
                The Parlay Genie can generate a recap summary and follow up questions from the transcription.<br/>
                To start using this feature, enable transcription in your settings.
              </div>
              <div>
                
              </div>
              <button @click="handleGoToActivateTranscription()" v-if="hasTeacherPermissionsInCourse" class="bg-pinky hover:bg-pinky-dark px-5 py-3 rounded-full text-white font-poppins font-medium">
                Activate Transcription
              </button>
            </div>
           
          </div>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
  import Axios from 'axios';
  import Vue from 'vue';
  import { mapGetters } from 'vuex';
  import moment from 'moment';
import { EventBus } from '../../../utils/event-bus';

  export default Vue.extend({
    data() {
      return {
        transcription: [],
        isLoading: false,
        selectedComponent: "timeline",
        isRecapOpen: true,
        isGeneratingRecap: false,
        summary: null,
      };
    },
    computed: {
      ...mapGetters([
        'getCurrentDiscussion',
        'hasTeacherPermissionsInCourse'
      ]),
      
    },
    methods: {
      handleGoToActivateTranscription: function() {
        EventBus.$emit('handleTab', 'settings');
      },
      handleRefreshDiscussion: async function() {
        await this.handleGetDiscussionWord();
      },
      handleGetDiscussionWord: async function() {
        try {
          this.isLoading = true;
          const transcription = await Axios.get(`/api/transcriptions/${this.getCurrentDiscussion.id}/transcription`);
          this.transcription = transcription.data.data;
          this.isLoading = false;
        } catch (error) { 
          console.log(error);
        }
      },
      handleOpenGenieRecap: function() {
        this.isRecapOpen = !this.isRecapOpen;
      },
      handleShowTranscriptDate(date) {
        return moment(date).format('LLLL');
      },
      setTurnIcon(type) {
        const icons = {
          'new_idea': 'fa-play',
          'build_on': 'fa-square',
          'challenge': 'fa-circle',
          'question': 'fa-circle-question',
          'hand_raise': 'fa-chalkboard-user'
        };
        const iconClass = icons[type];
        return iconClass ? `<i class="fas ${iconClass}" style="color: white"></i>` : '';
      },
      setTurnProperties(turnType) {
        const turns = {
          'new_idea': {
            title: this.$t('new_idea'),
            backgroundColor: 'bg-primary'
          },
          'build_on': {
            title: this.$t('build_on'),
            backgroundColor: 'bg-green-700'
          },
          'challenge': {
            title: this.$t('challenge'),
            backgroundColor: 'bg-pinky'
          },
          'question': {
            title: this.$t('question'),
            backgroundColor: 'bg-darkBlue-dark'
          },
          'hand_raise': {
            title: this.$t('added_by_teacher'),
            backgroundColor: 'bg-orange'
          }
        };
        return turns[turnType] || { title: '', backgroundColor: '' };
      },
      handleChangeComponent: function(component) {
        this.selectedComponent = component;
      }
    },
    mounted: async function() {
      const { handleGetDiscussionWord } = this;
      await handleGetDiscussionWord();
    }
  });

</script>

<style scoped>
  .genie-background {
    background: rgb(12, 112, 168);
    background: linear-gradient(
      47deg,
      rgba(12, 112, 168, 0.2) 0%,
      rgba(255, 35, 165, 0.3) 100%
    );
  }
</style>



// WEBPACK FOOTER //
// LDTranscriptionsTab.vue?d68e7dd8